import {
  PartnerIncomeTaxReadingProgressDto,
  PartnerIncomeTaxReadingStage,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';

/** Texto e barra do aviso de andamento da leitura (card 3772). */
export interface ReadingProgressView {
  title: string;
  detail: string;
  /** Fração lida com sucesso, 0 a 100: trecho azul da barra (e o valor do `progressbar`). */
  readPercent: number;
  /** Fração que terminou com falha, 0 a 100: trecho vermelho logo depois dos lidos. */
  failedPercent: number;
  /** Fração sendo lida agora, 0 a 100: largura do trecho animado logo depois do cheio. */
  readingPercent: number;
}

/** Duração legível: "45 s", "3 min", "1 h 05 min". Abaixo de 1 s vira "1 s" (acabou de começar). */
export function formatDuration(seconds: number): string {
  const total = Math.max(1, Math.round(seconds));
  if (total < 60) return `${total} s`;
  const minutes = Math.round(total / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${String(rest).padStart(2, '0')} min` : `${hours} h`;
}

/**
 * Tempo típico arredondado, sem precisão falsa: "menos de 1 min", "cerca de 2 min". A mediana oscila alguns segundos a
 * cada documento lido; o analista só precisa da ordem de grandeza.
 */
export function formatTypicalDuration(seconds: number): string {
  if (seconds < 60) return 'menos de 1 min';
  return `cerca de ${formatDuration(seconds)}`;
}

/**
 * Aviso da rodada de leitura: "Nesta leitura: 1 de 3 documentos lidos" e o detalhe "1 lendo · 1 na fila · 1 com falha
 * · cada documento leva menos de 1 min". O título conta só os lidos (falha não é "pronto"; ela aparece no detalhe e no
 * aviso vermelho). Os números vêm prontos do backend (`readingProgress`); aqui só se monta o texto.
 */
export function describeReadingProgress(
  progress: PartnerIncomeTaxReadingProgressDto
): ReadingProgressView {
  const total = Math.max(progress.total, 1);
  const read = Math.max(progress.finished - progress.failed, 0);
  const parts = [
    progress.reading ? `${progress.reading} lendo` : null,
    progress.queued ? `${progress.queued} na fila` : null,
    progress.failed ? `${progress.failed} com falha` : null,
    progress.typicalSeconds
      ? `cada documento leva ${formatTypicalDuration(progress.typicalSeconds)}`
      : null,
  ].filter((part): part is string => !!part);
  const percent = (value: number) => Math.round((Math.min(value, total) / total) * 100);
  // Cada trecho cabe no que sobra depois dos anteriores: o arredondamento nunca passa a barra de 100%.
  const readPercent = percent(read);
  const failedPercent = Math.min(percent(progress.failed), 100 - readPercent);
  const readingPercent = Math.min(percent(progress.reading), 100 - readPercent - failedPercent);

  return {
    title: `Nesta leitura: ${read} de ${progress.total} ${progress.total === 1 ? 'documento lido' : 'documentos lidos'}`,
    detail: parts.join(' · '),
    readPercent,
    failedPercent,
    readingPercent,
  };
}

/**
 * Tempo de um documento em leitura, para a linha embaixo do selo (que já diz "Na fila" ou "Lendo"): "Aguardando há
 * 30 s" na fila, "Começou há 2 min" sendo lido, "Aguardando o IRPF do sócio" no documento de apoio. Sem data (leitura
 * pedida antes do card 3772), `null`: o selo basta.
 */
export function describeStageElapsed(
  stage: PartnerIncomeTaxReadingStage,
  elapsedSeconds: number | null
): string | null {
  if (stage === 'waiting') return 'Aguardando o IRPF do sócio';
  if (elapsedSeconds == null) return null;
  const label = stage === 'reading' ? 'Começou' : 'Aguardando';
  return `${label} há ${formatDuration(elapsedSeconds)}`;
}
